import { Link } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { useDongClaims } from "@/hooks/useDongClaims";
import { useStudents } from "@/hooks/useStudents";
import { fmtYearMonth } from "@/lib/format";
import { claimMatchesStudent, claimLabel } from "@/lib/claims";
import { useMemo } from "react";

export default function DriverHome() {
  const { user } = useAuth();
  const ym = fmtYearMonth();
  const { claims } = useDongClaims(ym);
  const myClaims = claims.filter((c) => c.driverUid === user?.uid);

  const { students } = useStudents();
  const mine = useMemo(
    () =>
      students.filter(
        (s) => !s.courierFlag && myClaims.some((c) => claimMatchesStudent(c, s)),
      ),
    [students, myClaims],
  );

  const done = mine.filter((s) => s.deliveredMonths?.includes(ym)).length;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">🚚 오늘의 배송</h1>
      <div className="grid grid-cols-3 gap-2">
        <Stat title="담당(동/학교)" value={myClaims.length} />
        <Stat title="담당 가구" value={mine.length} />
        <Stat title={`${ym} 완료`} value={`${done}/${mine.length}`} />
      </div>

      <div className="card p-4">
        <h3 className="font-bold mb-3">내 담당 (동/학교)</h3>
        {myClaims.length === 0 ? (
          <div className="text-sm text-slate-500">
            아직 이번달 담당 동 이름을 선택하지 않았습니다.
            <Link to="/driver/claim" className="ml-2 text-milk-600 underline">담당 선택 →</Link>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {myClaims.map((c) => (
              <span key={c.id} className="chip bg-milk-100 text-milk-700">
                {claimLabel(c)} · {c.studentCount}명
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <QuickLink to="/driver/claim" title="이번달 동 이름 선택" desc="무상우유 대상 전체 리스트 중 담당 동 체크" />
        <QuickLink to="/driver/route" title="오늘 경로" desc="AI 최적화된 배송 순서, 턴바이턴 길안내" />
        <QuickLink to="/driver/stats" title="내 실적" desc="일/월별 배송 수와 누적 통계" />
      </div>
    </div>
  );
}

function Stat({ title, value }: { title: string; value: number | string }) {
  return (
    <div className="card p-3">
      <div className="label">{title}</div>
      <div className="text-2xl font-extrabold mt-1 text-milk-700">{value}</div>
    </div>
  );
}

function QuickLink({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link to={to} className="card p-4 hover:bg-milk-50 transition">
      <div className="font-bold text-milk-700">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
    </Link>
  );
}
